import {
  intersectionResolver,
  literalResolver,
  noopResolver,
  objectResolver,
} from 'airship/utils/resolvers'
import {Resolver} from 'airship/utils/resolvers/types'
import {Option} from './Option'
import OptionGroup from './OptionGroup'

export {Option, OptionGroup}

export type DisabledOptionalItem = {
  enabled: false
  value: null
}

export const resolveDisabledOptionalItem: Resolver<DisabledOptionalItem> = objectResolver(
  {
    enabled: literalResolver([false] as const),
    value: literalResolver([null] as const),
  }
)

export type EnabledOptionalItem<T> = {
  enabled: true
  value: T
}

export const resolveEnabledOptionalItem: Resolver<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  EnabledOptionalItem<any>
> = objectResolver({
  enabled: literalResolver([true] as const),
  value: noopResolver,
})

export type OptionalItem<T extends NonNullable<unknown>> =
  | DisabledOptionalItem
  | EnabledOptionalItem<T>

export const resolveOptionalItem: Resolver<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  OptionalItem<any>
> = intersectionResolver(
  resolveDisabledOptionalItem,
  resolveEnabledOptionalItem
)

type Payload<T> = {
  payload: T
}

type Callable<T> = () => T

/**
 * Creates a default (disabled) optional item
 */
export function createDefaultOption(): DisabledOptionalItem {
  return {enabled: false, value: null}
}

/**
 * Creates an enabled option with the provided value
 * @param value The optional item's value
 */
export function createOptionWithValue<T>(value: T): EnabledOptionalItem<T> {
  return {value, enabled: true}
}

/**
 * Creates an action handler for toggling an optional item
 * @param defaultEnabledValue Value to provide when option is toggled on or a function that returns it
 */
export function optionToggler<T extends unknown>(
  defaultEnabledValue: T | Callable<T>
): (state: unknown, {payload}: {payload: boolean}) => OptionalItem<T> {
  return updater

  function updater(
    state: unknown,
    {payload}: {payload: boolean}
  ): OptionalItem<T> {
    if (payload) {
      const value = isCallable(defaultEnabledValue)
        ? defaultEnabledValue()
        : defaultEnabledValue
      return createOptionWithValue(value)
    }

    return createDefaultOption()
  }
}

/**
 * Action handler for updating an optional value
 * @param state Previous item's state
 * @param action Action with payload to update item's value
 */
export function optionalValueUpdater<T>(
  state: unknown,
  {payload}: Payload<T>
): EnabledOptionalItem<T> {
  return createOptionWithValue(payload)
}

/**
 * Determines if provided argument is a callable function for generating a default value for optionToggler
 * @param maybeCallable The item to check
 */
function isCallable(
  maybeCallable: unknown
): maybeCallable is Callable<unknown> {
  return typeof maybeCallable === 'function' && maybeCallable.length === 0
}
